<template>
  <div class="flex flex-col w-full h-full bg-surface-50 overflow-hidden" dir="rtl">
    <!-- Toolbar: desktop header row; on mobile the app header holds the title and "+" -->
    <div class="flex-none bg-white border-b border-surface-200 px-4 md:px-7 pt-3 md:pt-5 pb-3 md:pb-4 flex flex-col md:flex-row md:items-center gap-3 md:gap-4 md:flex-wrap">
      <h2 class="hidden md:block m-0 text-2xl font-extrabold text-surface-900">משימות</h2>

      <div class="scope-switch" role="tablist" aria-label="סינון לפי אחריות">
        <button
          v-for="option in scopeOptions"
          :key="option.value"
          type="button"
          role="tab"
          :aria-selected="scope === option.value"
          :class="{ active: scope === option.value }"
          @click="scope = option.value"
        >{{ option.label }}</button>
      </div>

      <div v-if="scope !== 'done'" class="flex gap-2 overflow-x-auto no-scrollbar">
        <button
          v-for="option in filterOptions"
          :key="option.value"
          type="button"
          class="filter-chip"
          :class="{ active: filter === option.value }"
          @click="filter = option.value"
        >{{ option.label }}</button>
      </div>

      <Button
        v-if="!isMobile"
        label="משימה חדשה"
        icon="pi pi-plus"
        class="md:ms-auto"
        @click="createTask"
      />
    </div>

    <div class="flex-1 min-h-0 flex gap-5 md:px-7 md:py-5">
      <!-- List -->
      <div class="flex-1 min-w-0 overflow-y-auto px-4 md:px-0 py-4 md:py-0 flex flex-col gap-4">
        <div v-if="loading" class="flex flex-col gap-3">
          <Skeleton v-for="i in 4" :key="i" height="4rem" border-radius="14px" />
        </div>

        <template v-else-if="visibleTasks.length">
          <template v-if="scope === 'done'">
            <TaskRow
              v-for="task in visibleTasks"
              :key="task.id"
              :task="task"
              :selected="!isMobile && selectedId === task.id"
              :busy="busyId === task.id"
              :show-assignee="scope === 'done' && canManage"
              @toggle="toggle"
              @open="open"
            />
          </template>
          <template v-else>
          <div v-for="group in groups" :key="group.key" class="flex flex-col gap-2">
            <span class="text-[13px] font-bold" :class="group.danger ? 'text-red-700' : 'text-surface-600'">
              {{ group.label }} · {{ group.tasks.length }}
            </span>
            <TaskRow
              v-for="task in group.tasks"
              :key="task.id"
              :task="task"
              :selected="!isMobile && selectedId === task.id"
              :busy="busyId === task.id"
              :show-assignee="scope === 'team'"
              @toggle="toggle"
              @open="open"
            />
          </div>
          </template>
        </template>

        <div v-else class="flex flex-col items-center text-center gap-2 pt-16 px-6">
          <i class="pi pi-check-square text-4xl text-surface-300" />
          <span class="text-sm text-surface-600">{{ emptyText }}</span>
          <Button v-if="scope !== 'done'" label="משימה חדשה" icon="pi pi-plus" text size="small" @click="createTask" />
        </div>
      </div>

      <!-- Desktop: selected task -->
      <div v-if="!isMobile" class="w-[400px] flex-none self-start bg-white border border-surface-200 rounded-2xl p-5">
        <TaskDetails
          v-if="selectedTask"
          :task="selectedTask"
          :busy="busyId === selectedTask.id"
          @toggle="toggle"
          @edit="edit"
        />
        <div v-else class="flex flex-col items-center text-center gap-2 py-10">
          <i class="pi pi-list text-3xl text-surface-300" />
          <span class="text-sm text-surface-500">בחרו משימה כדי לראות את הפרטים</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useToast } from 'vue-toastification';
import { type ITask, TaskStatusEnum, type TTasksScope } from '@torim-app/types';
import { useAdminStore, useAdminTasksStore } from '@app/stores';
import { AdminRouteNames } from '@app/router/route-names';
import { TasksApi } from '@app/api/admin/tasks-api';
import { useResponsive } from '@app/composables/responsive';
import { useHeaderAction } from '@app/composables/header-action';
import { trackEvent } from '@app/composables/posthog';
import TaskRow from './TaskRow.vue';
import TaskDetails from './TaskDetails.vue';
import { groupOpenTasks, matchesFilter, type TaskFilter } from './task-utils';
import { useTaskEditor } from './use-task-editor';

const adminStore = useAdminStore();
const tasksStore = useAdminTasksStore();
const toast = useToast();
const { isMobile } = useResponsive();
const { setHeaderAction } = useHeaderAction();
const { openTaskEditor } = useTaskEditor();

adminStore.setPage(AdminRouteNames.Tasks);

const canManage = computed(() => adminStore.getCanManageOthers);
const scope = ref<TTasksScope>('mine');
const filter = ref<TaskFilter>('all');
const tasks = ref<ITask[]>([]);
const loading = ref(true);
const busyId = ref<string>();
const selectedId = ref<string>();

const scopeOptions = computed(() => [
  { value: 'mine' as TTasksScope, label: scope.value === 'mine' && !loading.value ? `שלי · ${tasks.value.length}` : 'שלי' },
  ...(canManage.value ? [{ value: 'team' as TTasksScope, label: 'כל הצוות' }] : []),
  { value: 'done' as TTasksScope, label: 'בוצעו' },
]);

const filterOptions = computed(() => {
  const count = (f: TaskFilter) => tasks.value.filter(t => matchesFilter(t, f)).length;
  const auto = count('auto');
  return [
    { value: 'all' as TaskFilter, label: 'הכל' },
    { value: 'auto' as TaskFilter, label: auto ? `אוטומטיות · ${auto}` : 'אוטומטיות' },
    { value: 'customers' as TaskFilter, label: 'ללקוחות' },
    { value: 'recurring' as TaskFilter, label: 'חוזרות' },
  ];
});

const visibleTasks = computed(() =>
  scope.value === 'done' ? tasks.value : tasks.value.filter(t => matchesFilter(t, filter.value)),
);
const groups = computed(() => groupOpenTasks(visibleTasks.value));
const selectedTask = computed(() => tasks.value.find(t => t.id === selectedId.value));

const emptyText = computed(() => {
  if (scope.value === 'done') return 'עוד לא סומנו משימות כבוצעו ב-30 הימים האחרונים';
  if (filter.value !== 'all') return 'אין משימות פתוחות בסינון הזה';
  return 'אין משימות פתוחות. כל הכבוד!';
});

const load = async () => {
  try {
    tasks.value = await TasksApi.list(scope.value);
    if (!isMobile.value && !selectedTask.value) selectedId.value = visibleTasks.value[0]?.id;
  } catch (e) {
    console.error(e);
    toast.error('לא הצלחנו לטעון את המשימות');
  } finally {
    loading.value = false;
  }
};

const toggle = async (task: ITask) => {
  if (!task.id || busyId.value) return;
  busyId.value = task.id;
  const done = task.status !== TaskStatusEnum.Done;
  try {
    await tasksStore.setDone(task.id, done);
    trackEvent('task_toggled', { done, source: task.source, autoType: task.autoType });
    if (done) toast.success('המשימה סומנה כבוצעה');
  } catch (e) {
    console.error(e);
    toast.error('לא הצלחנו לעדכן את המשימה');
  } finally {
    busyId.value = undefined;
  }
};

const createTask = () => openTaskEditor();
const edit = (task: ITask) => openTaskEditor({ task });
// Desktop selects into the side panel; mobile goes straight to the sheet.
const open = (task: ITask) => (isMobile.value ? edit(task) : (selectedId.value = task.id));

watch(scope, () => {
  loading.value = true;
  selectedId.value = undefined;
  if (scope.value === 'done') filter.value = 'all';
  load();
});
watch(() => tasksStore.version, load);

onMounted(() => {
  trackEvent('tasks_page_viewed');
  setHeaderAction({ icon: 'pi-plus', onClick: createTask, ariaLabel: 'משימה חדשה' });
  load();
});
</script>

<style scoped lang="scss">
.scope-switch {
  @apply grid bg-surface-100 rounded-xl p-1 gap-1;
  grid-auto-flow: column;
  grid-auto-columns: minmax(0, 1fr);

  @media (min-width: 768px) {
    @apply flex rounded-[10px];
  }

  button {
    @apply h-9 md:px-4 rounded-[9px] text-sm text-surface-600 transition-colors whitespace-nowrap;

    &.active {
      @apply bg-white text-surface-900 font-bold shadow-sm;
    }
  }
}

.filter-chip {
  @apply flex-none h-8 px-3 rounded-full border border-surface-200 bg-white text-[13px] text-surface-600;

  &.active {
    @apply border-primary bg-primary-50 text-primary-700 font-semibold;
  }
}

.no-scrollbar {
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
}
</style>
